import { gql } from 'apollo-boost'

export const UserFragment = gql`
  fragment UserFragment on User {
    id
    name
    email
    company
    rights
    deactivatedAt
    userToProjects {
      id
      role
      roleId
      project {
        id
        name
      }
    }
  }
`

export const BuildingFragment = gql`
  fragment BuildingFragment on Building {
    id
    name
    code
    description
    sortOrder
    createdAt
    updatedAt
    floorCount
    snagCount
    openSnagCount
  }
`

export const FloorFragment = gql`
  fragment FloorFragment on Floor {
    id
    name
    level
    description
    sortOrder
    createdAt
    updatedAt
    snagCount
    openSnagCount
    blueprintFile {
      id
      filename
      size
      mimetype
      key
      blueprintDzi {
        id
        dziUrl
        tilesBaseUrl
        width
        height
      }
      jobs {
        id
        type
        status
        progress
        currentStep
        error
      }
    }
    building {
      id
      name
      code
    }
  }
`

export const SnagFragment = gql`
  fragment SnagFragment on Snag {
    id
    number
    displayNumber
    title
    description
    status
    priority
    dueDate
    resolvedAt
    verifiedAt
    createdAt
    updatedAt
    pinX
    pinY
    infrastructureType
    floor {
      id
      name
      level
      blueprintFile {
        id
        blueprintDzi {
          id
          dziUrl
          tilesBaseUrl
          width
          height
        }
      }
      building {
        id
        name
        code
        project {
          id
          name
        }
      }
    }
    assignedTo {
      id
      name
      email
    }
    createdBy {
      id
      name
      email
    }
    organization {
      id
      name
    }
    files {
      id
      filename
      size
      mimetype
      key
    }
  }
`

export const BidderFragment = gql`
  fragment BidderFragment on Bidder {
    id
    name
    contactName
    email
    tags
    phone
    address
    deleted
    tenders {
      id
      name
    }
  }
`
export const PriceBreakdownNodeContentFragment = gql`
  fragment PriceBreakdownNodeContentFragment on PriceBreakdownNode {
    id
    name
    price
    unit
    acreage
    unitPrice
  }
`

export const PriceBreakdownNodeFragment = gql`
  fragment PriceBreakdownNodeFragment on PriceBreakdownNode {
    id
    name
    price
    unit
    acreage
    unitPrice
    quantity
    parentId
    rowId
    idx
  }
`

export const PriceBreakdownDataFragment = gql`
  fragment PriceBreakdownDataFragment on PriceBreakdown {
    disableUpload
    data {
      ...PriceBreakdownNodeFragment
      children {
        ...PriceBreakdownNodeFragment
        children {
          ...PriceBreakdownNodeFragment
          children {
            ...PriceBreakdownNodeFragment
            children {
              ...PriceBreakdownNodeFragment
            }
          }
        }
      }
    }
  }
  ${PriceBreakdownNodeFragment}
`

export const TenderBidderFragment = gql`
  fragment TenderBidderFragment on Bidder {
    id
    name
    contactName
    email
    address
  }
`

export const LogFragment = gql`
  fragment LogFragment on Log {
    id
    message
    data
    createdAt
    updatedAt
    type
    public
    mine
    children {
      id
      message
      createdAt
      updatedAt
      public
      mine
      user {
        id
        name
        email
      }
      bidder {
        id
        name
        contactName
        email
      }
      parent {
        id
      }
    }
    user {
      id
      name
      email
    }
    bidder {
      id
      name
      contactName
      email
    }
    parent {
      id
    }
  }
`

export const FileFragment = gql`
  fragment FileFragment on File {
    id
    filename
    size
    key
    mimetype
    createdAt
    user {
      id
      name
    }
    bidder {
      id
      name
    }
  }
`

export const BidFragment = gql`
  fragment BidFragment on Bid {
    id
    round
    bidder {
      id
      name
      contactName
      email
    }
    prices
    priceBreakdown {
      id
      file {
        id
      }
      disableUpload
    }
  }
`

export const TenderFragment = gql`
  fragment TenderFragment on Tender {
    id
    name
    description
    address
    status
    statusActions
    commencementDate
    submissionDate
    round
    hardcopyRequired
    hardcopyAddress
    enablePriceBreakdown
    updatedAt
    bids {
      id
      prices
      round
      priceBreakdown {
        id
        disableUpload
      }
    }
    packageManager {
      ...UserFragment
    }
    bidders {
      ...TenderBidderFragment
    }
    files {
      ...FileFragment
    }

    priceBreakdowns {
      id
      file {
        id
      }
      #data {
      # id
      # }
    }
  }

  ${UserFragment}
  ${TenderBidderFragment}
  ${FileFragment}
`

export const ProjectFragment = gql`
  fragment ProjectFragment on Project {
    id
    name
    description
    address
    tenders {
      id
      name
      bidders {
        id
        name
      }
    }
    users {
      id
      user {
        id
        name
      }
    }
  }
`

// ============================================
// DOC CONTROL FRAGMENTS
// ============================================

export const DocFolderFragment = gql`
  fragment DocFolderFragment on DocFolder {
    id
    name
    description
    parentId
    organizationId
    projectId
    createdAt
    updatedAt
    documentCount
    activeShareLinkCount
    restricted
    effectivePermission {
      level
      source
    }
    createdBy {
      id
      name
    }
  }
`

export const DocDocumentFragment = gql`
  fragment DocDocumentFragment on DocDocument {
    id
    name
    description
    folderId
    organizationId
    createdAt
    updatedAt
    revisionCount
    activeShareLinkCount
    filename
    mimetype
    size
    previewUrl
    createdBy {
      id
      name
    }
    currentRevision {
      id
      version
      uploadedAt
      comment
      fileId
      downloadUrl
      previewUrl
      file {
        id
        filename
        size
        mimetype
      }
    }
  }
`

export const DocRevisionFragment = gql`
  fragment DocRevisionFragment on DocRevision {
    id
    documentId
    version
    uploadedAt
    comment
    isCurrentRevision
    downloadUrl
    previewUrl
    uploadedBy {
      id
      name
    }
    file {
      id
      filename
      mimetype
      size
    }
  }
`

export const DocFolderPermissionFragment = gql`
  fragment DocFolderPermissionFragment on DocFolderPermission {
    id
    folderId
    userId
    roleId
    level
    user {
      id
      name
      email
    }
    projectRole {
      id
      name
    }
  }
`

export const DocShareLinkFragment = gql`
  fragment DocShareLinkFragment on DocShareLink {
    id
    token
    folderId
    documentId
    hasPassword
    expiresAt
    accessCount
    lastAccessedAt
    createdAt
    url
    isExpired
    createdBy {
      id
      name
    }
  }
`

export const DocTransmittalFragment = gql`
  fragment DocTransmittalFragment on DocTransmittal {
    id
    name
    description
    reference
    confidential
    status
    sentAt
    expiresAt
    token
    createdAt
    updatedAt
    itemCount
    recipientCount
    url
    isExpired
    createdBy {
      id
      name
    }
  }
`

export const DocActivityLogFragment = gql`
  fragment DocActivityLogFragment on DocActivityLog {
    id
    action
    folderId
    documentId
    revisionId
    transmittalId
    shareLinkId
    metadata
    ipAddress
    createdAt
    user {
      id
      name
    }
  }
`

// ============================================
// MEETINGS
// ============================================

export const MeetingOccurrenceFragment = gql`
  fragment MeetingOccurrenceFragment on MeetingOccurrence {
    id
    occurrenceNumber
    scheduledAt
    finalizedAt
    finalizedBy {
      id
      name
      email
    }
    agendaSnapshot
    minutesSnapshot
    minutesPdfUrl
    filesSnapshot
    externalGuestsSnapshot
    attendeesSnapshot
    createdAt
  }
`

export const AgendaSubItemFragment = gql`
  fragment AgendaSubItemFragment on AgendaSubItem {
    id
    orderIndex
    title
    notes
    kind
    status
    dueDate
    occurrenceId
    lockedAt
    seriesId
    previousVersionId
    lineageId
    createdAt
    updatedAt
    assignedTo {
      id
      name
      email
    }
  }
`

export const ActionItemUpdateFragment = gql`
  fragment ActionItemUpdateFragment on ActionItemUpdate {
    id
    body
    locked
    createdAt
    author {
      id
      name
    }
    occurrence {
      id
      occurrenceNumber
      scheduledAt
    }
  }
`

export const ActionItemOccurrenceSnapshotFragment = gql`
  fragment ActionItemOccurrenceSnapshotFragment on ActionItemOccurrenceSnapshot {
    id
    occurrenceId
    title
    description
    status
    priority
    dueDate
    completedAt
    assignedToName
    createdAt
  }
`

export const ActionItemFragment = gql`
  fragment ActionItemFragment on ActionItem {
    id
    title
    description
    status
    priority
    originOccurrenceNumber
    lineageId
    dueDate
    completedAt
    createdAt
    updatedAt
    assignedTo {
      id
      name
      email
    }
    createdBy {
      id
      name
    }
    linkedSnag {
      id
      number
      displayNumber
      title
      infrastructureType
    }
    linkedDoc {
      id
      name
    }
    agendaItem {
      id
      title
    }
    agendaOrderIndex
    files {
      id
      filename
      size
      mimetype
      url
    }
  }
`

export const AgendaItemFragment = gql`
  fragment AgendaItemFragment on AgendaItem {
    id
    orderIndex
    title
    notes
    createdAt
    updatedAt
    responsible {
      id
      name
      email
    }
    files {
      id
      filename
      size
      key
      mimetype
      url
    }
    subItems {
      ...AgendaSubItemFragment
    }
    actionItems {
      ...ActionItemFragment
    }
  }
  ${AgendaSubItemFragment}
  ${ActionItemFragment}
`

export const MeetingFragment = gql`
  fragment MeetingFragment on Meeting {
    id
    number
    displayNumber
    title
    description
    confidential
    type
    status
    scheduledAt
    durationMinutes
    isVirtual
    onlineProvider
    onlineSyncError
    location
    virtualLink
    externalGuests {
      id
      name
      email
      company
      present
      orderIndex
    }
    presentAttendeeIds
    finalizedAt
    distributedAt
    invitationsSentAt
    invitationsSequence
    recurrence
    recurrenceUntil
    nextScheduledAt
    attendeeCount
    createdAt
    updatedAt
    organizer {
      id
      name
      email
    }
    createdBy {
      id
      name
    }
    project {
      id
      resolvedTimezone
    }
  }
`

export const MeetingFullFragment = gql`
  fragment MeetingFullFragment on Meeting {
    ...MeetingFragment
    minutesContent
    minutesRecordedAt
    minutesRecordedBy {
      id
      name
    }
    project {
      id
      name
      resolvedTimezone
    }
    attendees {
      id
      name
      email
    }
    agendaItems {
      ...AgendaItemFragment
    }
    actionItems {
      ...ActionItemFragment
    }
    files {
      id
      filename
      size
      key
      mimetype
      url
    }
    linkedSnags {
      id
      number
      displayNumber
      title
      status
      infrastructureType
    }
    linkedDocs {
      id
      name
    }
    linkedTenders {
      id
      name
    }
    occurrenceCount
    occurrences {
      id
      occurrenceNumber
      scheduledAt
      finalizedAt
    }
    lastOccurrence {
      ...MeetingOccurrenceFragment
    }
    activityLogs {
      id
      title
      description
      createdAt
      user {
        id
        name
      }
    }
  }
  ${MeetingFragment}
  ${MeetingOccurrenceFragment}
  ${AgendaItemFragment}
  ${ActionItemFragment}
`

// ==================== SUBMITTALS ====================

export const SubmittalRowFragment = gql`
  fragment SubmittalRowFragment on Submittal {
    id
    projectId
    number
    displayNumber
    title
    companyName
    status
    dueDate
    createdAt
    responsible { id name }
    specSection { id value }
    category { id value }
    subcategory { id value }
    discipline { id value }
    package { id value }
    currentRevision { id index }
    reviewProgress { decided total }
    currentOwners { id name }
  }
`

export const SubmittalWorkflowTemplateFragment = gql`
  fragment SubmittalWorkflowTemplateFragment on SubmittalWorkflowTemplate {
    id
    projectId
    name
    targetFolderId
    archived
    steps {
      id
      index
      name
      durationDays
      reviewers { id name email }
    }
  }
`

export const SubmittalDetailFragment = gql`
  fragment SubmittalDetailFragment on Submittal {
    ...SubmittalRowFragment
    description
    costContract { id number name }
    createdBy { id name }
    revisions {
      id
      index
      submittedAt
      submittedBy { id name }
      finalVerdict
      finalNote
      finalizedAt
      finalizedBy { id name }
      publishedFolderId
      publishedAt
      workflowPlan
      activeStepIndex
      attachments {
        id
        fileId
        file { id filename url size }
        docRevisionId
        docRevision { id documentId version file { id filename url } }
        addedBy { id name }
        createdAt
      }
      reviews {
        id
        reviewerId
        reviewer { id name }
        verdict
        comment
        decidedAt
        stepIndex
        dueDate
      }
    }
  }
  ${SubmittalRowFragment}
`

// ==================== SCHEDULE ====================
export const ScheduleStatsFragment = gql`
  fragment ScheduleStatsFragment on ScheduleStats {
    total
    completed
    onTrack
    atRisk
    delayed
    critical
    finishVarianceDays
  }
`
export const ScheduleFragment = gql`
  fragment ScheduleFragment on Schedule {
    id
    projectId
    name
    description
    status
    startDate
    defaultCalendarId
    statusDate
    activeBaselineId
    start
    finish
    activityCount
    createdAt
    updatedAt
    createdBy {
      id
      name
      email
    }
    stats {
      ...ScheduleStatsFragment
    }
  }
  ${ScheduleStatsFragment}
`
export const ScheduleActivityFragment = gql`
  fragment ScheduleActivityFragment on ScheduleActivity {
    id
    scheduleId
    parentId
    sortIndex
    wbsCode
    outlineLevel
    name
    notes
    owner
    externalUid
    durationDays
    isMilestone
    anchorStart
    isSummary
    constraintType
    constraintDate
    calendarId
    percentComplete
    rolledPercent
    actualStart
    actualFinish
    start
    finish
    lateStart
    lateFinish
    totalFloatDays
    isCritical
    health
    warningCodes
    baselineStart
    baselineFinish
    baselineDurationDays
  }
`
export const ScheduleDependencyFragment = gql`
  fragment ScheduleDependencyFragment on ScheduleDependency {
    id
    predecessorId
    successorId
    type
    lagDays
    predecessorScheduleId
    successorScheduleId
  }
`
export const ScheduleExternalActivityFragment = gql`
  fragment ScheduleExternalActivityFragment on ScheduleExternalActivity {
    id
    scheduleId
    scheduleName
    name
    wbsCode
    start
    finish
    isMilestone
  }
`
export const ScheduleCalendarFragment = gql`
  fragment ScheduleCalendarFragment on ScheduleCalendar {
    id
    name
    workingWeekdays
    archived
    exceptions {
      id
      name
      fromDate
      toDate
      working
    }
  }
`
export const ScheduleBaselineFragment = gql`
  fragment ScheduleBaselineFragment on ScheduleBaseline {
    id
    name
    createdAt
    activityCount
    createdBy {
      id
      name
    }
  }
`
export const ScheduleMutationResultFragment = gql`
  fragment ScheduleMutationResultFragment on ScheduleMutationResult {
    schedule {
      ...ScheduleFragment
    }
    changedActivities {
      ...ScheduleActivityFragment
    }
    deletedActivityIds
    dependencies {
      ...ScheduleDependencyFragment
    }
    deletedDependencyIds
    affectedSchedules {
      ...ScheduleFragment
    }
  }
  ${ScheduleFragment}
  ${ScheduleActivityFragment}
  ${ScheduleDependencyFragment}
`

export const CostContractViewValuesFragment = gql`
  fragment CostContractViewValuesFragment on CostContractViewValues {
    budget
    committed
    approvedChanges
    pendingChanges
    certified
    invoiced
    saving
    estimate
    projectedFinalCost
    investorBilled
    billingDifference
  }
`

export const CostValuesFragment = gql`
  fragment CostValuesFragment on CostValues {
    originalBudget
    transfers
    approvedBudgetChanges
    revisedBudget
    pendingBudgetChanges
    projectedBudget
    committed
    approvedContractChanges
    pendingContractChanges
    potentialChanges
    actual
    estimate
    projectedFinalCost
    variance
    investorBilled
    subcontractorBilled
    billingDifference
  }
`

export const CostControlSettingsFragment = gql`
  fragment CostControlSettingsFragment on CostControlSettings {
    projectId
    currency
    budgetLockedAt
    budgetLockedById
    alertThresholdPercent
  }
`

export const CostContractFragment = gql`
  fragment CostContractFragment on CostContract {
    id
    projectId
    kind
    number
    name
    counterpartyName
    counterpartyIco
    appointedBy
    contractType
    status
    mainValue
    signedAt
    startDate
    endDate
    notes
    tenderId
    tenderName
    bidderId
    rfqId
    rfqNumber
    lastApplicationStatus
    retentionPercent
    createdAt
    updatedAt
    totals {
      value
      approvedChanges
      pendingChanges
      currentValue
      invoiced
      remaining
    }
    levelOneIds
    lineCount
    applicationCount
  }
`

export const CostAttachmentFragment = gql`
  fragment CostAttachmentFragment on CostAttachment {
    id
    source
    name
    url
    version
    documentId
    currentVersion
    currentUrl
    documentDeleted
    addedByName
    createdAt
  }
`

export const CostChangeFragment = gql`
  fragment CostChangeFragment on CostChange {
    id
    projectId
    number
    kind
    status
    title
    reason
    cause
    convertedFromPotentialAt
    contractId
    contractName
    contractNumber
    contractKind
    rfqId
    rfqNumber
    total
    lineCount
    levelOneIds
    drawsProvisional
    takenOver
    sourcedRfqId
    sourcedRfqNumber
    createdByName
    submittedAt
    submittedByName
    decidedAt
    decidedByName
    decisionNote
    createdAt
    updatedAt
  }
`

export const CostInvoiceFragment = gql`
  fragment CostInvoiceFragment on CostInvoice {
    id
    projectId
    contractId
    contractName
    contractNumber
    counterpartyName
    number
    issueDate
    dueDate
    status
    note
    retentionPercent
    retention
    payable
    applicationId
    applicationNumber
    amount
    lineCount
    approvedAt
    approvedByName
    paidAt
    createdByName
    createdAt
    updatedAt
  }
`

export const CostExpenseFragment = gql`
  fragment CostExpenseFragment on CostExpense {
    id
    projectId
    nodeId
    nodeName
    nodeCode
    path
    description
    supplierName
    date
    amount
    createdByName
    createdAt
    updatedAt
  }
`

export const CostApplicationFragment = gql`
  fragment CostApplicationFragment on CostApplication {
    id
    projectId
    contractId
    contractName
    contractNumber
    counterpartyName
    number
    periodFrom
    periodTo
    status
    note
    returnNote
    requested
    certified
    invoiceId
    invoiceNumber
    investorInvoice {
      number
      issueDate
      dueDate
    }
    submittedAt
    submittedByName
    certifiedAt
    certifiedByName
    createdByName
    createdAt
    updatedAt
  }
`

export const CostRfqFragment = gql`
  fragment CostRfqFragment on CostRfq {
    id
    projectId
    number
    title
    description
    dueDate
    status
    nodeId
    nodeName
    nodePath
    contractId
    contractName
    contractNumber
    askedCount
    quotedCount
    winnerQuoteId
    winnerName
    winnerAmount
    awardedContractId
    awardedChangeId
    awardedChangeNumber
    awardedAt
    awardedByName
    sourceChangeId
    sourceChangeNumber
    createdByName
    createdAt
    updatedAt
  }
`
